import React, { useMemo } from 'react';
import cn from 'classnames';
import { COMPONENT_TYPE } from 'common/constants';
import { SelectionSingleType } from 'client/components/common/SelectionHint/types';
import FixedWrapperCSS from 'client/components/common/BuildingBlocks/FixedWrapper/FixedWrapper.scss';
import css from './AlignmentGuides.scss';

export enum Axis {
	x = 'x',
	y = 'y',
}

export type InterestPoint = {
	axis: Axis;
	value: number;
	id: string;
};

type GuideLineProps = {
	axis: Axis;
	value: number;
	intersection: Intersection;
};

export type Intersection = ReturnType<typeof checkIntersection>;

export type AlignmentGuidesProps = {
	debug?: boolean;
	onIntersection: (intersection: Intersection, interestPoint: InterestPoint) => void;
	selection: SelectionSingleType;
};

export enum IntersectionSide {
	start = 'start',
	middle = 'middle',
	end = 'end',
}

class Box {
	x: number;

	y: number;

	top: number;

	left: number;

	bottom: number;

	right: number;

	width: number;

	height: number;

	element: Element;

	constructor(element: Element) {
		this.element = element;

		const { bbox } = this;
		this.x = bbox.x;
		this.y = bbox.y;
		this.top = this.y;
		this.left = this.x;
		this.bottom = bbox.bottom;
		this.right = bbox.right;
		this.width = bbox.width;
		this.height = bbox.height;
	}

	get bbox() {
		return this.element.getBoundingClientRect();
	}

	get interestPoints(): Record<Axis, number[]> {
		return {
			[Axis.x]: [this.x, this.x + this.width / 2, this.right],
			[Axis.y]: [this.y, this.y + this.height / 2, this.bottom],
		};
	}
}

function checkIntersection(selectedBox: Box, interestPoint) {
	let result: null | Record<IntersectionSide, boolean> = null;
	const tolerance = 4;

	const [start, middle, end] = selectedBox.interestPoints[interestPoint.axis];

	const diff = {
		[IntersectionSide.start]: start - interestPoint.value,
		[IntersectionSide.middle]: middle - interestPoint.value,
		[IntersectionSide.end]: end - interestPoint.value,
	};

	const isStart = Math.abs(diff[IntersectionSide.start]) <= tolerance;
	const isMid = Math.abs(diff[IntersectionSide.middle]) <= tolerance;
	const isEnd = Math.abs(diff[IntersectionSide.end]) <= tolerance;

	if (isStart || isMid || isEnd) {
		result = {
			[IntersectionSide.start]: isStart,
			[IntersectionSide.middle]: isMid,
			[IntersectionSide.end]: isEnd,
		};
	}

	return {
		isSnap: isStart || isMid || isEnd,
		tolerance,
		result,
		diff,
		side: isStart ? IntersectionSide.start : isMid ? IntersectionSide.middle : isEnd ? IntersectionSide.end : null,
	};
}

function useBoxes(id: string) {
	const selected = document.getElementById(id) as HTMLElement;
	// list of Boxes to compare the intersection
	const boxes = useMemo(() => {
		const fla = document.querySelectorAll(`[data-bb="${COMPONENT_TYPE.FLOAT_ABOVE}"]`);
		const flb = document.querySelectorAll(`[data-bb="${COMPONENT_TYPE.FLOAT_BELOW}"]`);
		const content = document.querySelector(`[data-bb="${COMPONENT_TYPE.CONTENT}"]`);
		const parent = selected.parentElement;
		const set = new Set<Element>();

		const floatChildren: Element[] = [];
		[...fla, ...flb].forEach(element => {
			floatChildren.push(...element.children);
		});

		[...floatChildren, ...Array.from(content!.children), parent, ...Array.from(parent!.children)].forEach(
			element => {
				if (element) {
					const bb =
						element.className.indexOf(FixedWrapperCSS.fixed) > -1
							? element.firstElementChild /* fixed position blocks are wrapped with an extra element */
							: element;
					if (bb) set.add(bb);
				}
			}
		);

		const result: Box[] = [];

		[...set.keys()].forEach(element => {
			if (element.id !== id /* skip selected box */) {
				result.push(new Box(element));
			}
		});

		return result;
	}, [selected, id]);

	if (!selected) {
		return {};
	}

	const selectedBox = new Box(selected);

	return {
		boxes,
		selectedBox,
	};
}

const GuideLine: React.FC<GuideLineProps> = ({ value, intersection, axis }) => (
	<div
		className={cn(css.guideLine, {
			[css.x]: axis === Axis.x,
			[css.y]: axis === Axis.y,
			[css.hasIntersection]: !!intersection.result,
			[css.isSnap]: !!intersection.isSnap,
		})}
		style={{
			[axis === Axis.x ? 'left' : 'top']: intersection.result && intersection.result.end ? value - 1 : value,
		}}
	/>
);

interface IGuidelineMapItem {
	interestPoint: InterestPoint;
	intersection: ReturnType<typeof checkIntersection>;
	value: number;
	axis: Axis;
}

const AlignmentGuides: React.FC<AlignmentGuidesProps> = ({ debug = false, ...props }) => {
	const { boxes, selectedBox } = useBoxes(props.selection.id);

	/**
	 * Iterate over boxes to identify intersection points with a current selection and pick the ones that can snap to.
	 * Max. is 6 points, one start/mid/end point for each axis.
	 */
	const guidelines = useMemo(() => {
		const guidelinesMap = new Map<string, IGuidelineMapItem>();

		boxes?.forEach(box => {
			const { interestPoints } = box;
			const entries = Object.entries(interestPoints) as Array<[Axis, number[]]>;

			entries.forEach(([axis, points]) => {
				points.forEach((value, index) => {
					const interestPoint: InterestPoint = { axis, value, id: `${box.element.id}-${axis}-${index}` };
					const intersection = checkIntersection(selectedBox, interestPoint);

					if (intersection.isSnap) {
						const key = `${axis}.${intersection.side}`;
						const prevItem = guidelinesMap.get(key);
						const currDiff = intersection.side ? Math.abs(intersection.diff[intersection.side]) : null;
						const prevDiff =
							prevItem && intersection.side
								? Math.abs(prevItem.intersection.diff[intersection.side])
								: null;
						const isCloser = currDiff !== null && (prevDiff === null || currDiff < prevDiff);

						if (isCloser) {
							guidelinesMap.set(key, { interestPoint, intersection, value, axis });
						}
					}
				});
			});
		});

		return Array.from(guidelinesMap.values());
	}, [boxes, selectedBox]);

	return (
		<div className={css.guides}>
			{guidelines.map(guideline => {
				if (guideline.intersection.isSnap) {
					props.onIntersection(guideline.intersection, guideline.interestPoint);
				}

				return (
					<GuideLine
						key={guideline.interestPoint.id}
						value={guideline.value}
						intersection={guideline.intersection}
						axis={guideline.axis}
					/>
				);
			})}
		</div>
	);
};

export default AlignmentGuides;
